import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { useMetaTags } from '@/hooks/useMetaTags';
import PageHeader from '@/components/PageHeader';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';
import { optimizeCloudinaryUrl, safeLogRocketTrack } from '@/lib/utils';
import { useAlbumContext } from '@/contexts/AlbumProvider';
import { useBanStatus } from '@/hooks/useBanStatus';
import { useSettings } from "@/hooks/useSettings";
import { useAlbumNavigation } from "@/hooks/useAlbumNavigation";
import { useTranslation } from '@/hooks/useTranslation';
import { useAlbumInteractions } from '@/contexts/AlbumInteractionsContext';
import * as Sentry from "@sentry/react";
import StickyControlsBar from './components/StickyControlsBar';
import AlbumControls from './components/AlbumControls';
import AlbumGrid from './components/AlbumGrid';
import AlbumSlideshow from './components/AlbumSlideshow';
import {
  DEFAULT_PRELOAD_RANGE,
  DEEPLINK_PARAM_FOTO_ID,
  DRAG_DISTANCE_THRESHOLD_PX,
  DRAG_VELOCITY_THRESHOLD,
  INFINITE_SCROLL_ROOT_MARGIN,
  ITEMS_PER_PAGE,
  OPEN_SLIDESHOW_PRELOAD_RANGE,
  SETTINGS_KEYS,
} from './constants';
import {
  buildLoopedAssets,
  createUserLookup,
  filterRenderableMediaItems,
  findAssetIndexByFotoId,
  getUniqueUsersFromAssets,
  mapSupabaseRowsToMediaItems,
  sortByCreationDateDesc,
  type MediaItem,
  type SortBy,
} from './utils';

const Album = () => {
  const { user, loading: authLoading } = useAuth();
  const { albumId, albumSlug } = useAlbumContext();
  const { isBanned } = useBanStatus();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const cameFromUsuarioRef = useRef(false);
  const [assets, setAssets] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [userVotes, setUserVotes] = useState<Set<string>>(new Set());
  const [userCommentedMediaIds, setUserCommentedMediaIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingImages, setLoadingImages] = useState<Set<string>>(new Set());
  const [slideshowOpen, setSlideshowOpen] = useState(false);
  const [slideshowIndex, setSlideshowIndex] = useState(0);
  const [preloadedImages, setPreloadedImages] = useState<Set<string>>(new Set());
  const [showPreOverlay, setShowPreOverlay] = useState(false);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [isVotesOpen, setIsVotesOpen] = useState(false);

  // Cache local de imágenes ya descargadas
  const imageCacheRef = useRef<Map<string, HTMLImageElement>>(new Map());

  // Sort and filter states
  const [sortBy, setSortBy] = useState<SortBy>('recent');
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [hideMyPhotos, setHideMyPhotos] = useState(false);
  const [pendingSelectedUsers, setPendingSelectedUsers] = useState<Set<string>>(new Set());
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const sortDropdownTriggerRef = useRef<HTMLButtonElement | null>(null);
  const filterDropdownTriggerRef = useRef<HTMLButtonElement | null>(null);

  const { getSetting, getBooleanSetting } = useSettings();
  const { getAlbumPath } = useAlbumNavigation();
  const { t, currentLanguage } = useTranslation();
  const {
    registerMediaItems,
    applyOptimisticCommentDelta,
    applyOptimisticVoteState,
    getVotesCount,
    getCommentsCount,
  } = useAlbumInteractions();

  // Check if single image download is enabled
  const isDownloadEnabled = getBooleanSetting(SETTINGS_KEYS.enableSingleImageDownload, false);
  // Check if single image description is enabled
  const isDescriptionEnabled = getBooleanSetting(SETTINGS_KEYS.enableSingleImageDescription, false);
  // Comments feature flag
  const isCommentsEnabled = getBooleanSetting(SETTINGS_KEYS.enableComments, false);
  // Likes feature flag
  const isLikesEnabled = getBooleanSetting(SETTINGS_KEYS.enableLikes, false);



  useMetaTags({
    title: t('digitalAlbum'),
    pathname: '/album'
  });


  // Reset pagination when filters or sorting change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedUsers, sortBy, hideMyPhotos]);

  // Sticky filters visibility - show when scrolled past the main filters bar
  useEffect(() => {
    const handleScroll = () => {
      if (filtersBarRef.current) {
        const rect = filtersBarRef.current.getBoundingClientRect();
        const filtersBottom = rect.bottom + window.scrollY;
        const currentY = window.scrollY;
        const hasPassed = currentY > filtersBottom;

        setHasPassedFilterBar(hasPassed);
        setIsStickyVisible(hasPassed);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll as any);
  }, []);

  const handleViewFilterClick = useCallback(() => {
    window.scrollTo({ top: 0 });
    // Open filter dropdown after scrolling starts
    setTimeout(() => {
      setIsFilterDropdownOpen(true);
      filterDropdownTriggerRef.current?.click();
    }, 100);
  }, []);

  const handleStickySortClick = useCallback(() => {
    window.scrollTo({ top: 0 });
    // Open sort dropdown after scrolling starts
    setTimeout(() => {
      setIsSortDropdownOpen(true);
      sortDropdownTriggerRef.current?.click();
    }, 100);
  }, []);

  // Initialize pending users when dropdown opens
  useEffect(() => {
    if (isFilterDropdownOpen) {
      setPendingSelectedUsers(new Set(selectedUsers));
    }
  }, [isFilterDropdownOpen, selectedUsers]);

  // TikTok-like scrolling states
  const [touchStartY, setTouchStartY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  // Swipe velocity tracking
  const lastYRef = useRef(0);
  const lastTimeRef = useRef(0);
  const velocityRef = useRef(0);

  // Sticky filters state and refs
  const [isStickyVisible, setIsStickyVisible] = useState(false);
  const [hasPassedFilterBar, setHasPassedFilterBar] = useState(false);
  const filtersBarRef = useRef<HTMLDivElement | null>(null);
  const chipsContainerRef = useRef<HTMLDivElement | null>(null);

  // Drag sensitivity config
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  // Track previous set of pending IDs to avoid full reloads when nothing changed

  // Refs for zoom prevention event listeners
  const preventZoomRef = useRef<((e: TouchEvent) => void) | null>(null);
  const preventGestureStartRef = useRef<((e: Event) => void) | null>(null);
  const pendingOwnVoteRealtimeRef = useRef<Set<string>>(new Set());
  const voteRefreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track images that have fully loaded to avoid re-adding to loading set
  const loadedImagesRef = useRef<Set<string>>(new Set());

  // Apply filters and sorting
  const filteredAssets = useMemo(() => {
    let filtered = assets;

    if (hideMyPhotos && user?.id) {
      filtered = filtered.filter((asset) => asset.uploaded_by !== user.id);
    }

    if (selectedUsers.size > 0) {
      filtered = filtered.filter((asset) => asset.uploaded_by && selectedUsers.has(asset.uploaded_by));
    }

    return filtered;
  }, [assets, hideMyPhotos, user?.id, selectedUsers]);
  const filteredAndSortedAssets = useMemo(() => {
    return [...filteredAssets].sort((a, b) => {
      if (sortBy === 'votes') {
        const votesDiff = getVotesCount(b.id, b.votes_count ?? 0) - getVotesCount(a.id, a.votes_count ?? 0);
        if (votesDiff !== 0) return votesDiff;
      } else if (sortBy === 'comments') {
        const commentsDiff = getCommentsCount(b.id, b.comments_count ?? 0) - getCommentsCount(a.id, a.comments_count ?? 0);
        if (commentsDiff !== 0) return commentsDiff;
      }

      const dateA = new Date(a.created_at || '').getTime();
      const dateB = new Date(b.created_at || '').getTime();
      const dateDiff = dateB - dateA;
      if (dateDiff !== 0) return dateDiff;

      return a.id.localeCompare(b.id);
    });
  }, [filteredAssets, sortBy, getVotesCount, getCommentsCount]);
  const canSortByVotes = useMemo(
    () =>
      isLikesEnabled &&
      filteredAssets.some((asset) => getVotesCount(asset.id, asset.votes_count ?? 0) > 0),
    [isLikesEnabled, filteredAssets, getVotesCount]
  );
  const canSortByComments = useMemo(
    () =>
      isCommentsEnabled &&
      filteredAssets.some((asset) => getCommentsCount(asset.id, asset.comments_count ?? 0) > 0),
    [isCommentsEnabled, filteredAssets, getCommentsCount]
  );
  const hasInteractionSortOptions = canSortByVotes || canSortByComments;

  useEffect(() => {
    if (sortBy === 'votes' && !canSortByVotes) {
      setSortBy('recent');
      return;
    }
    if (sortBy === 'comments' && !canSortByComments) {
      setSortBy('recent');
    }
  }, [sortBy, canSortByVotes, canSortByComments]);

  // Infinite loop helpers
  const loopedAssets = useMemo(() => buildLoopedAssets(filteredAndSortedAssets), [filteredAndSortedAssets]);
  const [pos, setPos] = useState(1); // visual position in loopedAssets
  const [noTransition, setNoTransition] = useState(false); // disable CSS transition during teleport

  useEffect(() => {
    registerMediaItems(assets);
  }, [assets, registerMediaItems]);

  useEffect(() => {
    if (loading) return;
    const node = loadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setCurrentPage((prev) =>
              prev * ITEMS_PER_PAGE < assets.length ? prev + 1 : prev
            );
          }
        });
      },
      { root: null, rootMargin: INFINITE_SCROLL_ROOT_MARGIN, threshold: 0.1 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [assets.length, loading]);


  useEffect(() => {
    // Fetch assets from Supabase
    fetchSupabaseAssets();

    // Fetch user votes if logged in
    if (user) {
      fetchUserVotes();
    }
  }, [user]);

  // Show pre-overlay as soon as fotoId is detected to avoid grid flash
  useEffect(() => {
    if (searchParams.get(DEEPLINK_PARAM_FOTO_ID)) {
      setShowPreOverlay(true);
    }
  }, [searchParams]);

  // Handle fotoId query parameter
  useEffect(() => {
    const fotoId = searchParams.get(DEEPLINK_PARAM_FOTO_ID);
    if (!fotoId || authLoading || loading) return;

    // Check if user is authenticated
    if (!user) {
      // Redirect to login with current URL as return destination
      const currentUrl = `${getAlbumPath('/album')}?fotoId=${fotoId}`;
      navigate(getAlbumPath(`/login?redirect=${encodeURIComponent(currentUrl)}`));
      return;
    }

    // Remember if we came from /usuario (preserved in location.state)
    const fromUsuario = Boolean((location.state as any)?.fromUsuario);
    cameFromUsuarioRef.current = fromUsuario;

    // Wait for assets to load, then find and open the specific asset
    if (assets.length > 0) {
      const assetIndex = findAssetIndexByFotoId(assets, fotoId);

      if (assetIndex !== -1) {
        // Clear the fotoId param from URL, but preserve state
        searchParams.delete(DEEPLINK_PARAM_FOTO_ID);
        navigate(getAlbumPath('/album'), { replace: true, state: { fromUsuario } });

        // Open slideshow with the found asset
        setTimeout(() => {
          openSlideshow(assetIndex);
        }, 100);
      }
    }
  }, [searchParams, user, authLoading, loading, assets, navigate, getAlbumPath, location.state]);


  const fetchSupabaseAssets = useCallback(async () => {
    try {
      // Use the new function that filters out banned users server-side
      const { data, error } = await supabase
        .rpc('get_media_with_votes_filtered', { p_album_id: albumId, p_exclude_flagged: true } as any);

      if (error) throw error;

      // Filter out media items with null uploaded_by and get unique user IDs
      const filteredData = (data || []).filter((row: any) => row.uploaded_by !== null);
      const userIds = [...new Set(filteredData.map((row: any) => row.uploaded_by))];

      // Fetch user information using the function that all users can access
      let usersData: any[] = [];
      if (userIds.length > 0) {
        const { data: users, error: usersError } = await supabase
          .rpc('get_user_display_info', { user_ids: userIds });

        if (!usersError && users) {
          usersData = users;
        }
      }

      const userLookup = createUserLookup(usersData);
      const mapped = mapSupabaseRowsToMediaItems(filteredData, userLookup);
      const filtered = filterRenderableMediaItems(mapped);
      const sorted = sortByCreationDateDesc(filtered);

      setAssets(sorted);

      // If there are pending items, schedule periodic refreshes handled by an effect below
    } catch (err) {
      Sentry.captureMessage(
        err instanceof Error ? err.message : String(err),
        'error'
      );
      console.error('Error fetching from Supabase:', err);
    } finally {
      setLoading(false);
    }
  }, [albumId]);


  const fetchUserVotes = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .rpc('get_user_votes', { user_uuid: user.id });

      if (error) throw error;

      const userVoteIds = new Set(data?.map((vote: any) => vote.media_id) || []);
      setUserVotes(userVoteIds);
    } catch (error) {
      Sentry.captureMessage(
        error instanceof Error ? error.message : String(error),
        'error'
      );
      console.error('Error fetching user votes:', error);
    }
  };

  useEffect(() => {
    if (!user || assets.length === 0) {
      setUserCommentedMediaIds(new Set());
      return;
    }

    let cancelled = false;

    const fetchUserCommentedMedia = async () => {
      try {
        const mediaIds = assets.map((asset) => asset.id);
        const chunkSize = 200;
        const commentedIds = new Set<string>();

        for (let i = 0; i < mediaIds.length; i += chunkSize) {
          const chunk = mediaIds.slice(i, i + chunkSize);
          const { data, error } = await supabase
            .from('comments')
            .select('media_id')
            .eq('user_id', user.id)
            .in('media_id', chunk);

          if (error) throw error;
          (data || []).forEach((row) => {
            if (row.media_id) commentedIds.add(row.media_id);
          });
        }

        if (!cancelled) {
          setUserCommentedMediaIds(commentedIds);
        }
      } catch (error) {
        console.error('Error fetching current user comments by media:', error);
      }
    };

    void fetchUserCommentedMedia();

    return () => {
      cancelled = true;
    };
  }, [user, assets]);

  const refreshVoteState = useCallback(async () => {
    if (!albumId) return;

    try {
      const { data, error } = await supabase
        .rpc('get_media_with_votes_filtered', { p_album_id: albumId, p_exclude_flagged: true } as any);

      if (error) throw error;

      const rows = (data || []) as Array<{ id: string; votes_count?: number; comments_count?: number }>;
      const countMap = new Map(
        rows.map((row) => [row.id, { votes_count: row.votes_count ?? 0, comments_count: row.comments_count ?? 0 }])
      );

      setAssets((prev) =>
        prev.map((asset) => {
          const next = countMap.get(asset.id);
          if (!next) return asset;
          return {
            ...asset,
            votes_count: next.votes_count,
            comments_count: next.comments_count,
          };
        })
      );

      if (user) {
        const { data: votesData, error: votesError } = await supabase
          .rpc('get_user_votes', { user_uuid: user.id });
        if (!votesError) {
          setUserVotes(new Set((votesData || []).map((vote: any) => vote.media_id)));
        }
      }
    } catch (error) {
      console.error('Error refreshing vote state:', error);
    }
  }, [albumId, user]);

  const scheduleVoteStateRefresh = useCallback(() => {
    if (voteRefreshTimeoutRef.current) return;
    voteRefreshTimeoutRef.current = setTimeout(() => {
      voteRefreshTimeoutRef.current = null;
      void refreshVoteState();
    }, 250);
  }, [refreshVoteState]);

  useEffect(() => {
    if (!albumId) return;

    const channel = supabase
      .channel(`album-media-votes-${albumId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'media_votes',
        },
        (payload: { eventType: 'INSERT' | 'DELETE' | 'UPDATE'; old?: { media_id?: string; user_id?: string }; new?: { media_id?: string; user_id?: string } }) => {
          if (payload.eventType !== 'INSERT' && payload.eventType !== 'DELETE') return;

          const row = payload.eventType === 'DELETE' ? payload.old : payload.new;
          const mediaId = row?.media_id as string | undefined;
          const voterId = row?.user_id as string | undefined;
          if (!mediaId) {
            scheduleVoteStateRefresh();
            return;
          }

          if (user?.id && voterId === user.id && pendingOwnVoteRealtimeRef.current.has(mediaId)) {
            pendingOwnVoteRealtimeRef.current.delete(mediaId);
            return;
          }

          const delta = payload.eventType === 'INSERT' ? 1 : -1;

          setAssets((prev) => {
            if (!prev.some((asset) => asset.id === mediaId)) return prev;
            return prev.map((asset) =>
              asset.id === mediaId
                ? { ...asset, votes_count: Math.max(0, (asset.votes_count || 0) + delta) }
                : asset
            );
          });

          if (user?.id && voterId === user.id) {
            setUserVotes((prev) => {
              const next = new Set(prev);
              if (delta > 0) {
                next.add(mediaId);
              } else {
                next.delete(mediaId);
              }
              return next;
            });
          }

          scheduleVoteStateRefresh();
        }
      )
      .subscribe();

    const interval = window.setInterval(() => {
      void refreshVoteState();
    }, 4000);

    return () => {
      window.clearInterval(interval);
      if (voteRefreshTimeoutRef.current) {
        clearTimeout(voteRefreshTimeoutRef.current);
        voteRefreshTimeoutRef.current = null;
      }
      supabase.removeChannel(channel);
    };
  }, [albumId, user?.id, refreshVoteState, scheduleVoteStateRefresh]);

  const toggleVote = async (mediaId: string, uploadedBy: string) => {
    if (!user) {
      toast({
        title: "Inicia sesión",
        description: "Necesitas iniciar sesión para dar likes en las fotos.",
        variant: "destructive",
      });
      return;
    }

    // Check if user is banned
    if (isBanned) {
      toast({
        title: "No puedes interactuar",
        description: "Tu cuenta ha sido suspendida.",
        variant: "destructive",
      });
      return;
    }

    // Check if user is trying to vote on their own photo
    if (uploadedBy === user.id) {
      toast({
        title: "Es tu foto",
        description: "No puedes darle like a tus propias fotos.",
        variant: "destructive",
      });
      return;
    }

    const hasVoted = userVotes.has(mediaId);

    try {
      const { data, error } = await supabase
        .rpc('toggle_media_vote', {
          media_uuid: mediaId,
          user_uuid: user.id
        });

      if (error) throw error;

      const result = data as { action: string; vote_count: number };
      pendingOwnVoteRealtimeRef.current.add(mediaId);

      // Update local state optimistically
      if (result.action === 'removed') {
        setUserVotes(prev => {
          const newSet = new Set(prev);
          newSet.delete(mediaId);
          return newSet;
        });

      } else {
        setUserVotes(prev => new Set([...prev, mediaId]));
        safeLogRocketTrack("Foto Votada", {
          user_id: user.id,
          foto_id: mediaId,
          album_name: albumSlug || '',
        });
      }

      // Update vote count using the returned count
      setAssets(prev => prev.map(asset =>
        asset.id === mediaId
          ? { ...asset, votes_count: result.vote_count }
          : asset
      ));
      applyOptimisticVoteState(mediaId, result.action !== 'removed', result.vote_count);

    } catch (error) {
      Sentry.captureMessage(
        error instanceof Error ? error.message : String(error),
        'error'
      );
      console.error('Error toggling like:', error);
      toast({
        title: "Error",
        description: "No se pudo procesar tu like. Intenta de nuevo.",
        variant: "destructive",
      });
    }
  };

  const handleImageLoad = (imageUrl: string) => {
    loadedImagesRef.current.add(imageUrl);
    setLoadingImages(prev => {
      const newSet = new Set(prev);
      newSet.delete(imageUrl);
      return newSet;
    });
  };

  const handleImageError = (imageUrl: string) => {
    loadedImagesRef.current.add(imageUrl);
    setLoadingImages(prev => {
      const newSet = new Set(prev);
      newSet.delete(imageUrl);
      return newSet;
    });
  };

  const handleImageLoadStart = (imageUrl: string) => {
    setLoadingImages(prev => new Set(prev).add(imageUrl));
  };

  // Get unique users for filter dropdown (only includes non-banned, existing users since assets are pre-filtered)
  const uniqueUsers = useMemo(() => getUniqueUsersFromAssets(assets), [assets]);


  // Infinite scroll pagination
  const totalPages = Math.ceil(filteredAndSortedAssets.length / ITEMS_PER_PAGE);
  const startIndex = 0;
  const endIndex = Math.min(filteredAndSortedAssets.length, currentPage * ITEMS_PER_PAGE);
  const currentAssets = filteredAndSortedAssets.slice(startIndex, endIndex);

  // Ensure newly rendered images are marked as loading until they fire onLoad
  useEffect(() => {
    setLoadingImages(prev => {
      let changed = false;
      const next = new Set(prev);
      // Add only images that are not loaded yet and not already marked as loading
      currentAssets.forEach(a => {
        if (a.resource_type === 'image' && a.secure_url) {
          const url = a.secure_url;
          if (!loadedImagesRef.current.has(url) && !prev.has(url)) {
            next.add(url);
            changed = true;
          }
        }
      });
      // Prune entries no longer visible
      Array.from(next).forEach(url => {
        if (!currentAssets.some(a => a.secure_url === url)) {
          next.delete(url);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [currentAssets]);

  // Image preloading function
  const preloadImages = useCallback((centerIndex: number, range = DEFAULT_PRELOAD_RANGE) => {
    const preloadPromises: Promise<void>[] = [];

    // Calculate range of images to preload
    const startIdx = Math.max(0, centerIndex - range);
    const endIdx = Math.min(filteredAndSortedAssets.length - 1, centerIndex + range);

    for (let i = startIdx; i <= endIdx; i++) {
      if (i !== centerIndex && filteredAndSortedAssets[i] && filteredAndSortedAssets[i].resource_type === 'image') {
        // Use the same transformation as slideshow/modal (isModal: true, maxWidth: 1900)
        const optimizedUrl = optimizeCloudinaryUrl({
          originalUrl: filteredAndSortedAssets[i].secure_url,
          width: filteredAndSortedAssets[i].width,
          height: filteredAndSortedAssets[i].height,
          isModal: true,
          maxWidth: 1900
        });
        // Check cache before preloading
        if (imageCacheRef.current.has(optimizedUrl)) {
          continue; // ya cacheada, no volver a descargar
        }

        // Only preload if not already preloaded
        if (!preloadedImages.has(optimizedUrl)) {
          // Mark as preloaded before creating the image to avoid race conditions
          setPreloadedImages(prev => new Set(prev).add(optimizedUrl));
          const preloadPromise = new Promise<void>((resolve) => {
            const img = new window.Image();
            img.onload = () => {
              resolve();
            };
            img.onerror = () => resolve(); // Still resolve on error to continue
            img.src = optimizedUrl;
            imageCacheRef.current.set(optimizedUrl, img);
          });
          preloadPromises.push(preloadPromise);
        }
      }
    }

    // Preload in batches to avoid overwhelming the network
    return Promise.allSettled(preloadPromises);
  }, [filteredAndSortedAssets, preloadedImages]);

  // TikTok-like slideshow functions
  const openSlideshow = (assetIndex: number) => {
    const globalIndex = startIndex + assetIndex;
    setSlideshowIndex(globalIndex);
    setPos(globalIndex + 1); // account for leading clone
    setSlideshowOpen(true);

    safeLogRocketTrack("Abrió al Slideshow", {
      user_id: user?.id,
      album_name: albumSlug || '',
    });
    // Prevent zoom on iOS by adding touch event listeners
    preventZoomRef.current = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    preventGestureStartRef.current = (e: Event) => {
      e.preventDefault();
    };

    document.addEventListener('touchstart', preventZoomRef.current, { passive: false });
    document.addEventListener('touchmove', preventZoomRef.current, { passive: false });
    document.addEventListener('gesturestart', preventGestureStartRef.current, { passive: false });

    // Preload a wider range (5) when opening slideshow for smooth navigation
    preloadImages(globalIndex, OPEN_SLIDESHOW_PRELOAD_RANGE);
  };

  const closeSlideshow = () => {
    // Compute the asset we were on before closing
    const targetAsset = filteredAndSortedAssets[slideshowIndex];

    setSlideshowOpen(false);
    setIsCommentsOpen(false); // Close comments panel when slideshow closes
    setIsVotesOpen(false);

    // Ensure the grid has enough pages to render the target asset
    if (targetAsset) {
      const requiredPage = Math.ceil((slideshowIndex + 1) / ITEMS_PER_PAGE);
      setCurrentPage((p) => Math.max(p, requiredPage));
      // After the overlay closes and grid renders, scroll to the thumbnail
      setTimeout(() => {
        const el = document.getElementById(`thumb-${targetAsset.id}`);
        if (el) {
          el.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'center', inline: 'nearest' });
        } else {
          // Try again on next frame in case layout hasn't settled
          requestAnimationFrame(() => {
            const el2 = document.getElementById(`thumb-${targetAsset.id}`);
            if (el2) el2.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'center', inline: 'nearest' });
          });
        }
      }, 50);
    }

    // Remove zoom prevention event listeners
    if (preventZoomRef.current) {
      document.removeEventListener('touchstart', preventZoomRef.current);
      document.removeEventListener('touchmove', preventZoomRef.current);
    }

    if (preventGestureStartRef.current) {
      document.removeEventListener('gesturestart', preventGestureStartRef.current);
    }

    // Clean up refs
    preventZoomRef.current = null;
    preventGestureStartRef.current = null;
  };

  // Handle browser back navigation when slideshow is open
  useEffect(() => {
    if (!slideshowOpen) return;

    // If user came from /usuario via deeplink, do not intercept back button
    if (cameFromUsuarioRef.current) {
      return;
    }

    // Otherwise, behave like modal: intercept back and close slideshow
    window.history.pushState({ slideshow: true }, "");
    const handlePopState = (event: PopStateEvent) => {
      if (slideshowOpen) {
        closeSlideshow();
        window.history.pushState({ slideshow: true }, "");
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [slideshowOpen]);

  // Hide pre-overlay once slideshow is open
  useEffect(() => {
    if (slideshowOpen) {
      setShowPreOverlay(false);
    }
  }, [slideshowOpen]);

  const nextSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setPos((p) => p + 1);
    setSlideshowIndex((idx) => (idx + 1) % Math.max(filteredAndSortedAssets.length, 1));
  };

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setPos((p) => p - 1);
    setSlideshowIndex((idx) => (idx - 1 + Math.max(filteredAndSortedAssets.length, 1)) % Math.max(filteredAndSortedAssets.length, 1));
  };

  // Touch and mouse handlers for vertical scrolling (mobile + desktop)
  const handleTouchStart = (e: React.TouchEvent) => {
    const y = e.touches[0].clientY;
    setTouchStartY(y);
    lastYRef.current = y;
    lastTimeRef.current = performance.now();
    velocityRef.current = 0;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const y = e.touches[0].clientY;
    const now = performance.now();
    const dy = lastYRef.current - y;
    const dt = Math.max(1, now - lastTimeRef.current);
    velocityRef.current = dy / dt; // px per ms (positive = swipe up)

    const diff = touchStartY - y;
    setDragOffset(diff);

    lastYRef.current = y;
    lastTimeRef.current = now;
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    const absDist = Math.abs(dragOffset);
    const absVel = Math.abs(velocityRef.current);

    if (!isTransitioning) {
      if (absVel > DRAG_VELOCITY_THRESHOLD || absDist > DRAG_DISTANCE_THRESHOLD_PX) {
        if (dragOffset > 0) {
          nextSlide();
        } else if (dragOffset < 0) {
          prevSlide();
        }
      }
    }

    setDragOffset(0);
    setTouchStartY(0);
    velocityRef.current = 0;
  };

  // Mouse handlers for desktop drag
  const handleMouseDown = (e: React.MouseEvent) => {
    const y = e.clientY;
    setTouchStartY(y);
    lastYRef.current = y;
    lastTimeRef.current = performance.now();
    velocityRef.current = 0;
    setIsDragging(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const y = e.clientY;
    const now = performance.now();
    const dy = lastYRef.current - y;
    const dt = Math.max(1, now - lastTimeRef.current);
    velocityRef.current = dy / dt;

    const diff = touchStartY - y;
    setDragOffset(diff);

    lastYRef.current = y;
    lastTimeRef.current = now;
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);

    const absDist = Math.abs(dragOffset);
    const absVel = Math.abs(velocityRef.current);

    if (!isTransitioning) {
      if (absVel > DRAG_VELOCITY_THRESHOLD || absDist > DRAG_DISTANCE_THRESHOLD_PX) {
        if (dragOffset > 0) {
          nextSlide();
        } else if (dragOffset < 0) {
          prevSlide();
        }
      }
    }

    setDragOffset(0);
    setTouchStartY(0);
    velocityRef.current = 0;
  };

  // Wheel handler for desktop scroll
  const handleWheel = (e: React.WheelEvent) => {
    if (isTransitioning) return;

    // Reduced threshold from 50 to 20 for more responsive wheel scrolling
    if (e.deltaY > 20) {
      nextSlide();
    } else if (e.deltaY < -20) {
      prevSlide();
    }
  };

  // Share function
  const shareAsset = async (assetUrl: string, uploadedByName: string) => {
    const fotoId = assetUrl.split('/').pop()?.split('.')[0];
    const shareUrl = `https://app.bloomkeep.site/s/${albumSlug}/${fotoId}`;


    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Compartir...',
          // text: `¡Mirá esta foto!`,
          url: shareUrl,
        });

        safeLogRocketTrack("Compartió Foto (Slideshow)", {
          user_id: user?.id,
          foto_id: fotoId,
          album_name: albumSlug || '',
        });
      } catch (error) {
        // console.log('Error sharing:', error);
      }
    } else {
      // Fallback to copying to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Enlace copiado al portapapeles');

      } catch (error) {
        // console.log('Error copying to clipboard:', error);
      }
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!slideshowOpen || isTransitioning) return;

      if (e.key === 'ArrowUp') {
        prevSlide();
      } else if (e.key === 'ArrowDown') {
        nextSlide();
      } else if (e.key === 'Escape') {
        closeSlideshow();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [slideshowOpen, isTransitioning]);

  // Preload slideshow images
  useEffect(() => {
    if (!slideshowOpen) return;
    // Preload a smaller range (2) on slide change for performance
    preloadImages(slideshowIndex, DEFAULT_PRELOAD_RANGE);
  }, [slideshowOpen, slideshowIndex, preloadImages]);

  // Reset description expansion and close comments when slide changes
  useEffect(() => {
    setIsDescriptionExpanded(false);
    setIsCommentsOpen(false);
    setIsVotesOpen(false);
  }, [slideshowIndex]);

  // Manage body scroll when slideshow is open/closed
  useEffect(() => {
    const STYLE_ID = 'slideshow-body-overlay-style';

    // Inject the CSS rule once
    const ensureStyleTag = () => {
      let styleEl = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = STYLE_ID;
        styleEl.textContent = `
          body.slideshow-open, body.slideshow-open #root{
            background-color: black;
          }
          body.slideshow-open #root > *:not(#gallery-slideshow) {
            opacity: 0 !important;
          }
        `;
        document.head.appendChild(styleEl);
      }
    };

    if (slideshowOpen) {
      ensureStyleTag();
      document.body.style.overflow = 'hidden';
      document.body.classList.add('slideshow-open');
    } else {
      document.body.style.overflow = 'auto';
      document.body.classList.remove('slideshow-open');
      // Clean up any legacy overlay node from prior implementations
      const existingOverlay = document.getElementById('slideshow-overlay');
      if (existingOverlay) existingOverlay.remove();
    }

    // Cleanup function to ensure scroll and overlay are reset
    return () => {
      document.body.style.overflow = 'auto';
      document.body.classList.remove('slideshow-open');
      const existingOverlay = document.getElementById('slideshow-overlay');
      if (existingOverlay) existingOverlay.remove();
    };
  }, [slideshowOpen]);

  const albumHeaderTitle =
    (getSetting('text_home_h1', '') || '').trim() ||
    (getSetting('text_album_page_title', '') || '').trim() ||
    t('digitalAlbum');

  return (
    <>
      <div className='min-h-screen bg-background'>
        {/* Header */}
        <PageHeader title={albumHeaderTitle} />

        <StickyControlsBar
          visible={isStickyVisible}
          hasAssets={assets.length > 0}
          showSort={hasInteractionSortOptions}
          onSortClick={handleStickySortClick}
          onFilterClick={handleViewFilterClick}
          t={t}
        />

        {/* Loading State */}
        {loading && (
          <div className='max-w-4xl mx-auto px-4 py-8'>
            <div className='grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4'>
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index} className='aspect-square rounded-lg' />
              ))}
            </div>
          </div>
        )}

        {!loading && (
          <div className='max-w-4xl mx-auto px-4 py-8 pb-24'>
            {assets.length > 0 && (
              <AlbumControls
                showSort={hasInteractionSortOptions}
                sortBy={sortBy}
                setSortBy={setSortBy}
                canSortByVotes={canSortByVotes}
                canSortByComments={canSortByComments}
                isSortDropdownOpen={isSortDropdownOpen}
                setIsSortDropdownOpen={setIsSortDropdownOpen}
                sortDropdownTriggerRef={sortDropdownTriggerRef}
                users={uniqueUsers}
                selectedUsers={selectedUsers}
                setSelectedUsers={setSelectedUsers}
                hideMyPhotos={hideMyPhotos}
                setHideMyPhotos={setHideMyPhotos}
                currentUserId={user?.id}
                isFilterDropdownOpen={isFilterDropdownOpen}
                setIsFilterDropdownOpen={setIsFilterDropdownOpen}
                filterDropdownTriggerRef={filterDropdownTriggerRef}
                filtersBarRef={filtersBarRef}
                chipsContainerRef={chipsContainerRef}
                t={t}
              />
            )}
            <AlbumGrid
              assets={assets}
              currentAssets={currentAssets}
              filteredAndSortedAssets={filteredAndSortedAssets}
              startIndex={startIndex}
              openSlideshow={openSlideshow}
              loadMoreRef={loadMoreRef}
              setSelectedUsers={setSelectedUsers}
              setHideMyPhotos={setHideMyPhotos}
              setSortBy={setSortBy}
              userVotes={userVotes}
              userCommentedMediaIds={userCommentedMediaIds}
              isLikesEnabled={isLikesEnabled}
              isCommentsEnabled={isCommentsEnabled}
              getSetting={getSetting}
              getAlbumPath={getAlbumPath}
              t={t}
            />
          </div>
        )}

      </div>

      {/* Pre-slideshow overlay to prevent grid flash when opening via fotoId */}
      {showPreOverlay && (
        <div className="fixed inset-0 bg-black/90 z-[9998] flex items-center justify-center pointer-events-auto">
          <Loader2 className="w-8 h-8 text-white animate-spin" />
        </div>
      )}

      <AlbumSlideshow
        slideshowOpen={slideshowOpen}
        filteredAndSortedAssets={filteredAndSortedAssets}
        loopedAssets={loopedAssets}
        pos={pos}
        setPos={setPos}
        noTransition={noTransition}
        setNoTransition={setNoTransition}
        isDragging={isDragging}
        dragOffset={dragOffset}
        isCommentsOpen={isCommentsOpen}
        setIsCommentsOpen={setIsCommentsOpen}
        isVotesOpen={isVotesOpen}
        setIsVotesOpen={setIsVotesOpen}
        setIsTransitioning={setIsTransitioning}
        closeSlideshow={closeSlideshow}
        cameFromUsuario={cameFromUsuarioRef.current}
        navigateToUsuario={() => navigate(getAlbumPath('/usuario'))}
        getAssetIndexLabel={`${slideshowIndex + 1}/${filteredAndSortedAssets.length}`}
        slideshowIndex={slideshowIndex}
        isLikesEnabled={isLikesEnabled}
        isCommentsEnabled={isCommentsEnabled}
        isDownloadEnabled={isDownloadEnabled}
        isDescriptionEnabled={isDescriptionEnabled}
        isDescriptionExpanded={isDescriptionExpanded}
        setIsDescriptionExpanded={setIsDescriptionExpanded}
        user={user}
        userVotes={userVotes}
        userCommentedMediaIds={userCommentedMediaIds}
        currentLanguage={currentLanguage}
        albumSlug={albumSlug}
        toast={toast}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onWheel={handleWheel}
        onToggleVote={toggleVote}
        showDeleteAction={false}
        onCommentsCountChange={(delta, mediaId) => {
          const id = mediaId || filteredAndSortedAssets[slideshowIndex]?.id;
          if (!id) return;
          setAssets(prev => prev.map(a => a.id === id ? { ...a, comments_count: Math.max(0, (a.comments_count ?? 0) + delta) } : a));
          applyOptimisticCommentDelta(id, delta);

          if (!user) return;
          if (delta > 0) {
            setUserCommentedMediaIds((prev) => new Set(prev).add(id));
            return;
          }

          if (delta < 0) {
            void (async () => {
              const { count } = await supabase
                .from('comments')
                .select('id', { count: 'exact', head: true })
                .eq('media_id', id)
                .eq('user_id', user.id);

              setUserCommentedMediaIds((prev) => {
                const next = new Set(prev);
                if ((count || 0) > 0) {
                  next.add(id);
                } else {
                  next.delete(id);
                }
                return next;
              });
            })();
          }
        }}
        t={t}
      />



    </>
  );
};

export default Album;
